const normalizeVersion = (version: string): string => {
  return version.trim().replace(/^v/iu, '').split(/[+-]/u)[0] ?? '';
};

const parseVersionParts = (version: string): number[] | null => {
  const normalized = normalizeVersion(version);
  if (!/^\d+(?:\.\d+)*$/u.test(normalized)) {
    return null;
  }

  return normalized.split('.').map((part) => Number.parseInt(part, 10));
};

const isTargetVersionNewer = (currentVersion: string | null, targetVersion: string | null): boolean => {
  if (!targetVersion) {
    return false;
  }

  if (!currentVersion) {
    return true;
  }

  const targetParts = parseVersionParts(targetVersion);
  const currentParts = parseVersionParts(currentVersion);

  if (!targetParts || !currentParts) {
    return normalizeVersion(targetVersion) !== normalizeVersion(currentVersion);
  }

  const length = Math.max(targetParts.length, currentParts.length);
  for (let index = 0; index < length; index += 1) {
    const targetPart = targetParts[index] ?? 0;
    const currentPart = currentParts[index] ?? 0;

    if (targetPart > currentPart) {
      return true;
    }

    if (targetPart < currentPart) {
      return false;
    }
  }

  return false;
};

/**
 * npm 설치 명령에 넣어도 안전한 버전 문자열인지 판정한다(semver 형식만 허용).
 *
 * 실패 버전은 러너가 보고한 값이라 서버 검증이 뚫리면 셸 명령에 그대로 섞일 수 있다.
 * 명령을 만드는 쪽에서도 형식을 다시 확인해, 안전하지 않으면 명령 자체를 만들지 않는다.
 */
const SAFE_INSTALL_VERSION_PATTERN = /^v?\d+(?:\.\d+){0,2}(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;

export const isSafeInstallVersion = (version: string | null | undefined): version is string =>
  typeof version === 'string' && version.length <= 64 && SAFE_INSTALL_VERSION_PATTERN.test(version);

/**
 * 자동 업데이트 차단 배지를 띄울지 판정한다.
 *
 * 서버가 실패 상태를 해제하기 전이라도(예: 응답 캐시가 한 박자 늦을 때) 러너가 이미 실패
 * 대상 버전 이상으로 올라갔으면 배지를 감춰서, 이미 해결된 문제를 다시 보여주지 않는다.
 */
export const shouldShowRunnerUpdateFailure = (
  currentVersion: string | null,
  failedVersion: string | null | undefined,
): boolean => isTargetVersionNewer(currentVersion, failedVersion ?? null);

export const shouldShowPendingRunnerVersion = (currentVersion: string | null, pendingVersion: string | null): boolean =>
  isTargetVersionNewer(currentVersion, pendingVersion);
